import { SeoPage, CtaInline } from './SeoLayout';
import { getLocalBusinessSchema, getBreadcrumbSchema } from '../../data/businessInfo';

const articleSchema = (title, description, path) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  mainEntityOfPage: { '@type': 'WebPage', '@id': `https://farbwelt-kalender.de${path}` },
  author: {
    '@type': 'Organization',
    name: 'FarbWelt Maler Express Service',
    url: 'https://farbwelt-kalender.de'
  },
  inLanguage: 'de-DE'
});

export function RatgeberWohnungsuebergabe() {
  const path = '/ratgeber/wohnungsuebergabe';
  const title = 'Wohnung streichen vor der Übergabe – was gilt wirklich?';
  const desc = 'Was Vermieter wirklich verlangen dürfen, wann Sie streichen müssen und wann nicht — ehrliche Einordnung vom Malerbetrieb aus Dormagen.';
  return (
    <SeoPage
      title={`${title} | Ratgeber FarbWelt`}
      description={desc}
      canonicalPath={path}
      kicker="Ratgeber"
      heroTitle={title}
      heroSubtitle="Was Vermieter wirklich verlangen dürfen, wann Sie streichen müssen — und wo sich der Malerbetrieb rechnet."
      jsonLd={[
        getLocalBusinessSchema(),
        articleSchema(title, desc, path),
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'Ratgeber', item: '/ratgeber' },
          { name: 'Wohnungsübergabe', item: path }
        ])
      ]}
    >
      <h2>Muss ich beim Auszug wirklich streichen?</h2>
      <p>
        Kurze Antwort: <strong>meistens nicht so umfassend, wie Vermieter behaupten.</strong> Der
        BGH hat mehrfach entschieden, dass starre Fristen im Mietvertrag („alle 3 Jahre streichen")
        unwirksam sind. Sie schulden dem Vermieter grundsätzlich nur, was Sie zusätzlich zur normalen
        Abnutzung verursacht haben — und die Wohnung <em>„besenrein"</em> zu übergeben.
      </p>
      <p>
        Praktisch heißt das: Wenn Sie in einer weiß gestrichenen Wohnung eingezogen sind und die Wände
        durch normale Nutzung leicht vergilbt sind, müssen Sie in der Regel <strong>nicht</strong> neu
        streichen. Anders sieht es aus, wenn Sie in kräftigen Farben gestrichen haben (Rot, Schwarz,
        Blau) oder starke Beschädigungen vorliegen.
      </p>

      <h2>Wann Sie besser streichen sollten</h2>
      <ul>
        <li>Sie haben in kräftigen oder ungewöhnlichen Farben gestrichen (Vermieter kann Rückführung auf neutrale Farbe verlangen)</li>
        <li>Es gibt sichtbare Wasserränder, Nikotinbeläge oder starke Verschmutzungen</li>
        <li>Es gibt Löcher (Dübel, Nägel) in ungewöhnlich hoher Zahl</li>
        <li>Sie wollen die Kaution ohne Diskussion zurück und Streit vermeiden</li>
      </ul>

      <h2>Wann Sie NICHT streichen müssen</h2>
      <ul>
        <li>Sie ziehen nach einer kurzen Mietzeit aus und die Wände sind noch top</li>
        <li>Die Klauseln im Mietvertrag sind starre Zeitraum-Klauseln (oft unwirksam — im Zweifel Mieterschutz fragen)</li>
        <li>Sie haben in einer weißen Wohnung nur weiße Wände hinterlassen und die Abnutzung ist gering</li>
      </ul>

      <CtaInline text="Wohnungsübergabe steht an? Wir machen das schnell und günstig." />

      <h2>DIY oder Malerbetrieb — was lohnt sich?</h2>
      <p>
        Selbst streichen kostet Sie 1–2 volle Wochenenden Arbeit plus rund 60–150 € Material für eine
        durchschnittliche Wohnung. Ein Malerbetrieb bringt eine 2-Zimmer-Wohnung in 1–2 Tagen auf
        Übergabe-Standard — inklusive Bohrlöcher schließen, Kanten sauber abkleben, Rand streichen.
        Bei uns liegt eine typische Übergabe-Renovierung im mittleren dreistelligen Bereich, abhängig
        von Fläche und Zustand.
      </p>
      <p>
        Der wirtschaftliche Vorteil: Wenn Ihr Vermieter durch schlecht gemachte Eigenleistung Nachbesserung
        durch einen Fachbetrieb einfordert, wird es teuer — und Sie zahlen doppelt. Ein Festpreis vom
        Malerbetrieb bringt Ihnen Sicherheit UND Rechnung mit Arbeitsleistung, die Sie zu 20 % nach § 35a
        EStG steuerlich absetzen können.
      </p>

      <h2>Was Sie vor unserem Termin vorbereiten sollten</h2>
      <ul>
        <li>Möbel aus den Räumen räumen (oder mit uns absprechen)</li>
        <li>Bilder, Regale, Vorhänge abhängen</li>
        <li>Übergabeprotokoll oder Wünsche des Vermieters bereithalten</li>
        <li>Wenn möglich: alten Farbeimer/Farbcode aus dem Einzug parat haben</li>
      </ul>

      <h2>Häufige Fragen zur Übergabe-Renovierung</h2>
      <p><strong>Reicht ein einfacher Anstrich?</strong> In den meisten Fällen ja. Falls die Wand
      stark verfleckt ist, empfehlen wir 2 Anstriche.</p>
      <p><strong>Bekomme ich das an einem Tag hin?</strong> Bei einer klassischen 2-Zimmer-Wohnung
      schaffen wir das häufig an einem langen Werktag. 3-Zimmer eher 2 Tage.</p>
      <p><strong>Was ist mit Küche und Bad?</strong> Fliesenspiegel bleiben, Wände über den Fliesen
      werden mitgestrichen. Bei starkem Fettbefall in der Küche empfiehlt sich vorher eine Isolier-Grundierung.</p>
    </SeoPage>
  );
}

export function RatgeberTapeteEntfernen() {
  const path = '/ratgeber/tapete-entfernen';
  const title = 'Tapete entfernen: Anleitung, Werkzeug & Kostenüberblick';
  const desc = 'Alte Tapete richtig lösen, Untergrund vorbereiten, typische Fehler vermeiden — plus ehrliche Kostenschätzung von Ihrem Malerbetrieb in Dormagen.';
  return (
    <SeoPage
      title={`${title} | Ratgeber FarbWelt`}
      description={desc}
      canonicalPath={path}
      kicker="Ratgeber"
      heroTitle={title}
      heroSubtitle="Wie Sie alte Tapete sauber runterbekommen — und warum sich der Fachmann bei Vlies-Tapete oder Raufaser mit 3 Anstrichen manchmal wirklich lohnt."
      jsonLd={[
        getLocalBusinessSchema(),
        articleSchema(title, desc, path),
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'Ratgeber', item: '/ratgeber' },
          { name: 'Tapete entfernen', item: path }
        ])
      ]}
    >
      <h2>Vor dem Start: Welche Tapete haben Sie?</h2>
      <p>
        Die Art der Tapete entscheidet über den Aufwand. Fassen Sie eine Ecke an und ziehen leicht:
      </p>
      <ul>
        <li><strong>Vliestapete</strong> — trocken abziehbar, geht meist in ganzen Bahnen ab. Aufwand: gering.</li>
        <li><strong>Papiertapete</strong> — muss angefeuchtet werden, kommt oft in Fetzen. Aufwand: mittel.</li>
        <li><strong>Raufaser mit Anstrich</strong> — je nach Anzahl der Anstriche entweder wie Papiertapete oder bereits fast wie eine feste Schicht. Aufwand: hoch.</li>
        <li><strong>Kunststoff- oder Vinyltapete</strong> — obere Schicht abziehen, Trägerpapier separat entfernen. Aufwand: mittel bis hoch.</li>
      </ul>

      <h2>Werkzeug — was Sie brauchen</h2>
      <ul>
        <li>Tapetenperforator (Igel) oder Tapetenlöser</li>
        <li>Warmes Wasser + Spülmittel oder professioneller Tapetenablöser</li>
        <li>Farbroller oder Pflanzensprüher</li>
        <li>Spachtel (breit) und Cuttermesser</li>
        <li>Abdeckplane für den Boden (Papier oder dünnes Vlies)</li>
      </ul>

      <h2>Schritt-für-Schritt Anleitung</h2>
      <ol>
        <li><strong>Perforieren</strong> — Mit dem Tapetenigel gleichmäßig über die gesamte Fläche fahren. Nur so kommt Wasser durch die Farbschicht.</li>
        <li><strong>Einweichen</strong> — Warmes Wasser mit einem Schuss Spülmittel oder Ablöser flächig auf die Wand bringen (Roller ist bequemer als Sprühen).</li>
        <li><strong>10–15 Minuten warten</strong> — Nicht schneller sein wollen. Wenn die Tapete komplett feucht ist, geht sie deutlich leichter ab.</li>
        <li><strong>Ablösen</strong> — Mit dem Spachtel unten anfangen, in ganzen Bahnen nach oben ziehen. Bei Raufaser lieber in Streifen arbeiten.</li>
        <li><strong>Reste entfernen</strong> — Kleine Kleisterreste und Papierfetzen mit einem feuchten Schwamm und Spachtel wegholen.</li>
        <li><strong>Trocknen lassen</strong> — 24 Stunden. Dann prüfen, ob der Untergrund glatt und tragfähig ist.</li>
      </ol>

      <CtaInline text="Zu viel Aufwand? Wir übernehmen das ganze Zimmer inklusive Entsorgung." />

      <h2>Wenn der Untergrund kaputt ist</h2>
      <p>
        Häufigster Fehler beim DIY-Tapetenentfernen: mit dem Spachtel zu viel Putz beschädigen.
        Dann kommen Sie um Spachteln und Grundieren nicht herum. Kalkulieren Sie:
      </p>
      <ul>
        <li>Kleine Ausbesserungen: 1–2 Stunden Arbeit pro Zimmer</li>
        <li>Ganze Wand neu verspachteln: 4–6 Stunden pro Wand</li>
        <li>Grundierung + neuer Anstrich: 1 Tag pro Zimmer</li>
      </ul>

      <h2>Was kostet das professionell?</h2>
      <p>
        Grober Rahmen für Dormagen und Umgebung (Stand aktuell): Tapete entfernen liegt üblicherweise
        bei ca. 4–8 € pro m² Wandfläche — abhängig von Tapetenart und Zustand. Bei zusätzlichem Verspachteln
        und neuem Anstrich kommen weitere ca. 8–15 € pro m² dazu. Für einen konkreten Festpreis kommen
        wir kostenlos vorbei und messen alles aus.
      </p>
    </SeoPage>
  );
}

export function RatgeberWandfarbe() {
  const path = '/ratgeber/wandfarbe-auswaehlen';
  const title = 'Wandfarbe auswählen: Der ehrliche Ratgeber';
  const desc = 'Welche Wandfarbe für Wohnzimmer, Küche, Bad, Kinderzimmer? Wischbeständigkeit, Deckkraft, Farbwirkung — praktische Empfehlungen vom Malerbetrieb in Dormagen.';
  return (
    <SeoPage
      title={`${title} | Ratgeber FarbWelt`}
      description={desc}
      canonicalPath={path}
      kicker="Ratgeber"
      heroTitle={title}
      heroSubtitle="Deckkraft, Wischbeständigkeit, Farbwirkung — und welche Farbe wir in welchem Raum wirklich empfehlen."
      jsonLd={[
        getLocalBusinessSchema(),
        articleSchema(title, desc, path),
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'Ratgeber', item: '/ratgeber' },
          { name: 'Wandfarbe auswählen', item: path }
        ])
      ]}
    >
      <h2>Zuerst: Die Deckkraft-Klasse verstehen</h2>
      <p>
        Auf jedem Farbeimer stehen zwei Angaben, die Sie kennen sollten: <strong>Deckkraft-Klasse</strong>{' '}
        (1 = beste, 4 = schlechteste) und <strong>Nassabriebklasse</strong> (1 = beste, 5 = nur trocken abwischbar).
      </p>
      <ul>
        <li>Klasse 1 Deckkraft ⇒ ein Anstrich reicht auf ähnlichem Untergrund</li>
        <li>Klasse 2 ⇒ meistens 2 Anstriche nötig</li>
        <li>Klasse 3–4 ⇒ Sie werden 3 Anstriche und Nerven brauchen</li>
      </ul>
      <p>
        Baumarkt-Wandfarben zum Sonderpreis sind oft Klasse 2–3. Rechnen Sie ehrlich: 3 Eimer billige Farbe +
        Zeit kosten häufig mehr als 1–2 Eimer gute Farbe.
      </p>

      <h2>Nassabrieb — die unterschätzte Angabe</h2>
      <p>Wichtig, wenn Sie Kinder, Haustiere oder eine Küche haben:</p>
      <ul>
        <li><strong>Klasse 1–2</strong> ⇒ nass abwischbar, ideal für Küche, Flur, Kinderzimmer</li>
        <li><strong>Klasse 3</strong> ⇒ okay für Wohn- und Schlafzimmer</li>
        <li><strong>Klasse 4–5</strong> ⇒ Vorsicht — jede Reinigung nimmt Farbe mit</li>
      </ul>

      <h2>Unsere Empfehlungen pro Raum</h2>
      <h3>Wohnzimmer</h3>
      <p>Warmweiß oder Off-White (RAL 9010, RAL 9016 sind zu grell) für maximale Zeitlosigkeit. Wenn Sie
      Akzente wollen: <strong>eine</strong> Wand in gedecktem Ton (warmes Grau, sanftes Grün, gedämpftes Blau).
      Nicht alle vier Wände farbig — das erdrückt den Raum optisch.</p>

      <h3>Küche</h3>
      <p>Nassabrieb Klasse 1 oder eine spezielle Latex-Wandfarbe. Über dem Herd empfiehlt sich eine
      Isolier-Grundierung, sonst schlagen alte Fettreste irgendwann durch.</p>

      <h3>Bad</h3>
      <p>Schimmelresistente Farbe mit Latexanteil. Vor dem Anstrich unbedingt Untergrund entfetten und
      auf verdeckten Schimmel prüfen. Bei Schimmelbefall: NICHT einfach überstreichen — muss fachgerecht
      saniert werden, sonst kommt es zurück.</p>

      <h3>Kinderzimmer</h3>
      <p>Emissions- und geruchsarme Farbe (Blauer Engel oder EU Ecolabel), Nassabrieb Klasse 1–2 (Fingerabdrücke!).
      Zu grelle Signalfarben nur als Akzent — sie ermüden den Raum optisch.</p>

      <h3>Schlafzimmer</h3>
      <p>Ruhige, gedeckte Töne wirken besser als reines Weiß. Salbeigrün, gedämpftes Blau oder ein warmes
      Grau schaffen Ruhe. Nassabrieb hier weniger wichtig.</p>

      <CtaInline text="Wir liefern Farbe und Aufmaß in einem Termin." />

      <h2>Wie viel Farbe brauchen Sie?</h2>
      <p>Faustformel: <strong>Wandfläche in m² × 0,15 = Liter für einen Anstrich.</strong></p>
      <p>Beispiel Wohnzimmer 20 m² Grundfläche, 2,50 m Deckenhöhe:</p>
      <ul>
        <li>Wandumfang etwa 18 m × 2,50 m = 45 m² Wandfläche</li>
        <li>Abzug für Fenster/Türen: ca. 5 m² ⇒ 40 m² zu streichende Fläche</li>
        <li>40 × 0,15 = <strong>6 Liter</strong> für einen Anstrich, 12 Liter für zwei Anstriche</li>
      </ul>

      <h2>Wann wir Ihnen von DIY abraten</h2>
      <ul>
        <li>Deckenanstrich in Räumen &gt; 3 m Höhe — Verletzungsgefahr, Gerüst nötig</li>
        <li>Wände mit Nikotinbelag oder starken Wasserflecken — braucht Isolier-Grundierung</li>
        <li>Große Flächen (&gt; 60 m²) mit einheitlicher Farbwirkung — Ansatzstellen ohne Erfahrung fast unvermeidlich</li>
        <li>Frisch verspachtelte Wände — Grundierung + Rollertechnik entscheiden über das Endergebnis</li>
      </ul>
    </SeoPage>
  );
}
